import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { Sparkles, RefreshCw, AlertCircle, Calendar as CalIcon } from "lucide-react";
import api from "@/lib/api";
import { useData } from "@/lib/store";
import { useAuth } from "@/lib/auth";
import ItemCard from "@/components/ItemCard";
import { daysUntil, formatDue, TYPE_META } from "@/lib/utils";

export default function TodayPage() {
  const { user } = useAuth();
  const { items, spaces, refresh } = useData();
  const [params] = useSearchParams();
  const spaceFilter = params.get("space");
  const [brief, setBrief] = useState(null);
  const [briefLoading, setBriefLoading] = useState(true);

  const loadBrief = async () => {
    setBriefLoading(true);
    try {
      const r = await api.post("/brain/brief");
      setBrief(r.data.brief);
      // brief ticks recurring rules — pull any newly-generated bill items
      await refresh();
    } catch { setBrief("Welcome back. Take a breath — one thing at a time."); }
    finally { setBriefLoading(false); }
  };

  useEffect(() => { loadBrief(); /* eslint-disable-next-line */ }, [user?.id]);

  const filtered = spaceFilter ? items.filter((i) => i.space_id === spaceFilter) : items;
  const today = new Date(); today.setHours(0, 0, 0, 0);
  const needsYou = filtered
    .filter((i) => i.status !== "done" && i.due_date && daysUntil(i.due_date) <= 0)
    .sort((a, b) => (a.due_date || "").localeCompare(b.due_date || ""));

  const renewals = filtered
    .filter((i) => (i.type === "renewal" || i.type === "bill") && i.status !== "done" && i.due_date)
    .sort((a, b) => a.due_date.localeCompare(b.due_date))
    .slice(0, 6);

  return (
    <div className="grid grid-cols-1 md:grid-cols-12 gap-6 animate-fade-in-up">
      {/* Daily brief hero */}
      <div className="col-span-full md:col-span-8 relative overflow-hidden rounded-hero md:rounded-heroLg bg-surface-container p-8 md:p-10">
        <div className="absolute -top-16 -left-16 w-64 h-64 rounded-full bg-primary-container/70 blur-[80px] pointer-events-none" />
        <div className="absolute -bottom-20 -right-10 w-72 h-72 rounded-full bg-tertiary-container/70 blur-[90px] pointer-events-none" />
        <div className="relative z-10">
          <div className="flex items-center justify-between mb-4">
            <div className="inline-flex items-center gap-2 text-primary text-sm font-medium">
              <Sparkles className="w-4 h-4" /> AI Daily Brief
            </div>
            <button
              data-testid="refresh-brief-btn"
              onClick={loadBrief}
              className="p-2 rounded-full hover:bg-primary/10 active:scale-95 transition-[transform,background-color] duration-200 ease-md text-surface-onVariant"
              aria-label="Refresh brief"
            >
              <RefreshCw className={`w-4 h-4 ${briefLoading ? "animate-spin" : ""}`} />
            </button>
          </div>
          <p data-testid="daily-brief-text" className="text-xl md:text-2xl font-medium text-surface-on leading-snug tracking-tight">
            {briefLoading ? "Reading your admin…" : brief}
          </p>
          <div className="mt-6 flex flex-wrap gap-2">
            {spaces.map((s) => (
              <div key={s.id} className="inline-flex items-center gap-2 bg-surface rounded-full px-3 py-1.5 text-xs text-surface-on border border-outline-variant">
                <span className="w-2 h-2 rounded-full" style={{ backgroundColor: s.color }} />
                {s.name}
                <span className="text-surface-onVariant ml-1">{s.open_count ?? 0}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Renewals card */}
      <div className="col-span-full md:col-span-4 rounded-hero bg-surface-container p-6">
        <div className="flex items-center gap-2 mb-4">
          <CalIcon className="w-4 h-4 text-tertiary" />
          <h2 className="text-lg font-medium text-surface-on">Upcoming renewals</h2>
        </div>
        {renewals.length === 0 ? (
          <p className="text-sm text-surface-onVariant">Nothing on the horizon. Nice.</p>
        ) : (
          <div className="space-y-2" data-testid="renewals-list">
            {renewals.map((r) => <ItemCard key={r.id} item={r} />)}
          </div>
        )}
      </div>

      {/* Needs you now */}
      <div className="col-span-full rounded-hero bg-surface-container p-6 md:p-8">
        <div className="flex items-center gap-2 mb-4">
          <AlertCircle className="w-4 h-4 text-danger" />
          <h2 className="text-lg font-medium text-surface-on">Needs you now</h2>
          <span className="ml-2 text-sm text-surface-onVariant">{needsYou.length}</span>
        </div>
        {needsYou.length === 0 ? (
          <div className="text-center py-10">
            <div className="text-4xl">🌿</div>
            <p className="text-surface-onVariant mt-2 text-sm">Inbox zero for today. Well done.</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="needs-you-list">
            {needsYou.map((i) => <ItemCard key={i.id} item={i} />)}
          </div>
        )}
      </div>
    </div>
  );
}
